import { create } from "zustand";
import { persist } from "zustand/middleware";
import { brand } from "../brand.js";

/**
 * "Your country" (owner, 2026-10-20; Settings → Your country): whose categories the guide, Movies
 * and Series list first, and whose channels Home's Live Now features. Per device, not per profile:
 * it is about where the TV is.
 *
 * Automatic (the default, `chosen: null`) is a white-label build's own country when it sets one,
 * else the TV's region (the WebView's language carries the system's: "en-GB" → GB), else the US.
 */
export interface CountryStore {
  /** An ISO 3166 code the viewer picked, or null for automatic. */
  chosen: string | null;
  setChosen: (country: string | null) => void;
}

const CODE = /^[A-Z]{2}$/;

export const useCountry = create<CountryStore>()(
  persist(
    (set) => ({
      chosen: null,
      setChosen: (country) => set({ chosen: country && CODE.test(country) ? country : null }),
    }),
    { name: "slique-country" },
  ),
);

/** The region of the first language that names one ("en-GB" → "GB"), or null. */
export function regionOfLanguages(languages: readonly string[]): string | null {
  for (const language of languages) {
    const region = /^[a-z]{2,3}[-_](?:[A-Za-z]{4}[-_])?([A-Za-z]{2})\b/.exec(language)?.[1]?.toUpperCase();
    if (region && CODE.test(region)) return region;
  }
  return null;
}

/** What automatic means on this device. */
export function automaticCountry(): string {
  const tenant = brand().country;
  if (tenant && CODE.test(tenant)) return tenant;
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const languages = nav ? (nav.languages?.length ? nav.languages : [nav.language]) : [];
  return regionOfLanguages(languages.filter(Boolean)) ?? "US";
}

/** The viewer's country, for code outside React. */
export function viewerCountry(): string {
  return useCountry.getState().chosen ?? automaticCountry();
}

/** The viewer's country, for a component: re-renders when it changes. */
export function useViewerCountry(): string {
  const chosen = useCountry((s) => s.chosen);
  return chosen ?? automaticCountry();
}
